<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Sample Result Form Data Loading...
  </div>
  <div v-else class="animated fadeIn">
    <b-card no-body>
      <b-card-header>
        <span class="text-muted text-uppercase">Sample Result Configuration
        </span>
      </b-card-header>
      <b-card-body>
        <b-form-group label="Task Type" label-cols="2">
          <b-row>
            <b-col class="pr-0">
              <b-button :variant="task_type === 'prep' ? 'primary' : ''" block id="Prep" @click="(e) => updateTaskType(e, 'prep')">Prep</b-button>
            </b-col>
            <b-col>
              <b-button :variant="task_type === 'test' ? 'primary' : ''" block id="Test" @click="(e) => updateTaskType(e, 'test')">Test</b-button>
            </b-col>
          </b-row>
        </b-form-group>
        <b-form-group label="Analyte" label-cols="2">
          <b-select v-model="form_data.analyte_id">
            <b-select-option v-for="analyte in analyte_collection" :value="analyte.analyte_id" :key="analyte.id" :disabled="analyte.disabled || false">{{ analyte.name }}</b-select-option>
          </b-select>
        </b-form-group>
        <b-form-group label="Instrument" label-cols="2" :disabled="!task_type">
          <b-select v-model="selected_instrument">
            <b-select-option v-for="instrument in instrument_options" :value="instrument.id" :key="instrument.id">{{ instrument.name }}</b-select-option>
          </b-select>
        </b-form-group>
        <!-- <b-form-group label="Instrument Jig" label-cols="2">
          <b-select v-model="form_data.instrument_jig_id" :disabled="!form_data.instrument_id">
            <b-select-option v-for="jig in jig_options" :value="jig.id" :key="jig.id">{{ jig.name }}</b-select-option>
          </b-select>
        </b-form-group> -->
        <b-form-group label="Schedulable Task" label-cols="2">
          <b-select v-model="form_data.schedulable_task_id" :disabled="!form_data.instrument_id">
            <b-select-option v-for="task in task_options" :value="task.id" :key="task.id">{{ task.name }}</b-select-option>
          </b-select>
        </b-form-group>
        <b-form-group label="Lab Instrument" label-cols="2">
          <b-select v-model="form_data.instrument_instance_id" :disabled="!form_data.schedulable_task_id">
            <b-select-option v-for="instrument_instance in instrument_instance_options" :value="instrument_instance.id" :key="instrument_instance.id">{{ instrument_instance.name }}</b-select-option>
          </b-select>
        </b-form-group>
        <b-form-group label="Analysis" label-cols="2">
          <b-select v-model="form_data.analysis_id" :disabled="!form_data.schedulable_task_id">
            <b-select-option v-for="analysis in analysis_options" :value="analysis.id" :key="analysis.id">{{ analysis.measurement_method || analysis.name }}</b-select-option>
          </b-select>
        </b-form-group>
        <b-form-group label="Unit of Measure" label-cols="2">
          <b-select v-model="form_data.unit_of_measure_id" :disabled="!form_data.analysis_id">
            <b-select-option v-for="unit in measure_unit_options" :value="unit.id" :key="unit.id">{{ unit.name }}</b-select-option>
          </b-select>
        </b-form-group>
        <b-form-group label="Raw Result" label-cols="2">
          <b-form-input type="number" v-model="form_data.raw_result" />
        </b-form-group>
        <b-form-group label="Formatted Result" label-cols="2">
          <b-form-input v-model="form_data.formatted_result" type="text" />
        </b-form-group>
      </b-card-body>
    </b-card>
    <b-row>
      <b-col>
        <b-button @click="submitForm" class="float-right py-3 px-5" variant="success"><i class="fal fa-address-book" /> Save</b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { cloneAttributes } from "@/shared/utils";
import { getCollection } from "@/shared/fetch-utils";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";

const AnalyteResultRepository = LabRepositoryFactory.get("analyteResult");
const SampleRepository = LabRepositoryFactory.get("sample");

export default {
  name: "SampleResultForm",
  data() {
    return {
      loading: true,
      task_type: null,
      analysis_collection: null,
      analyte_collection: null,
      instrument_collectiion: null,
      instrument_jig_collectiion: null,
      task_collection: null,
      form_data: {
        instrument_id: null,
        sample_id: this.$route.params.sample_id || null,
        analysis_id: null,
        analyte_id: null,
        instrument_instance_id: null,
        schedulable_task_id: null,
        schedulable_task_type: null,
        instrument_jig_id: null,
        raw_result: null,
        formatted_result: null,
        unit_of_measure_id: null,
      },
      payload: {
        sort: [
          {
            logic: "sortAddAsc",
            field: "name",
          },
        ],
        limit: "all",
        message: this.$bvToast,
      },
      default_option: [
        {
          id: null,
          name: "No available options",
          disabled: true,
        },
      ],
    };
  },
  computed: {
    instrument_options() {
      return this.formCollectionOptions(
        "instrument_id",
        this.instrument_collectiion
      );
    },
    jig_options() {
      return this.formCollectionOptions(
        "instrument_jig_id",
        this.instrument_jig_collectiion,
        "name",
        "jig"
      );
    },
    task_options() {
      return this.formCollectionOptions(
        "schedulable_task_id",
        this.task_collection
      );
    },
    analysis_options() {
      return this.formTargetOptions(
        "analysis_id",
        this.task_collection,
        this.form_data.schedulable_task_id,
        "analyses"
      );
    },
    measure_unit_options() {
      if (this.form_data.schedulable_task_id && this.form_data.analysis_id) {
        const task = this.task_collection.models.find(
          ({ id }) => id === this.form_data.schedulable_task_id
        );
        return this.formTargetOptions(
          "unit_of_measure_id",
          task.analyses,
          this.form_data.analysis_id,
          "units",
          "abbrev"
        );
      } else {
        return this.default_option;
      }
    },
    instrument_instance_options() {
      return this.formTargetOptions(
        "instrument_instance_id",
        this.task_collection,
        this.form_data.schedulable_task_id,
        "instrument_instances"
      );
    },
    selected_instrument: {
      get() {
        return this.form_data.instrument_id;
      },
      set(value) {
        this.form_data.instrument_id = value;
        this.form_data.schedulable_task_id = null;
        this.form_data.instrument_instance_id = null;
        this.form_data.instrument_jig_id = null;
        this.fetchInstrumentConnections();
      },
    },
  },
  methods: {
    updateTaskType(e, type) {
      const { id } = e.target;
      this.form_data.schedulable_task_type = `App\\Models\\${id}Task`;
      this.task_type = type;
      this.form_data.instrument_id = null;
    },
    formOptions(form_field, collection, label = "name", child_field) {
      if (collection.length) {
        collection.length === 1 &&
          (this.form_data[form_field] = collection[0].id);
        const options = collection.map((item) => {
          return {
            id: item.id,
            name: child_field ? item[child_field][label] : item[label],
          };
        });
        return options;
      } else {
        return this.default_option;
      }
    },
    formCollectionOptions(form_field, collection, label = "name", child_field) {
      return collection
        ? this.formOptions(form_field, collection.models, label, child_field)
        : this.default_option;
    },
    formTargetOptions(
      form_field,
      collection,
      target_id,
      target_field,
      label = "name",
      child_field
    ) {
      if (collection && target_id) {
        const models = collection.models || collection;
        const check = models.find(({ id }) => id === target_id);

        return check && check[target_field].length
          ? this.formOptions(
            form_field,
            check[target_field],
            label,
            child_field
          )
          : this.default_option;
      }

      return this.default_option;
    },
    async submitForm() {
      const payload = cloneAttributes(this.form_data, true, true, [
        "instrument_id",
      ]);

      try {
        const { data } = await AnalyteResultRepository.create(payload);

        this.$bvToast.toast("Result saved successfully.", {
          title: "Result Creation completed.",
          variant: "success",
        });
        this.$router.push(`/samples/${data.sample_id}`);
      } catch ({ response }) {
        const { status, data } = response;
        this.$bvToast.toast(`Code: ${status}. ${data.message}`, {
          title: "Server Error.",
          variant: "danger",
        });
      }
    },
    async fetchInstrumentConnections() {
      const payload = {
        filters: [
          {
            logic: "filterEquals",
            field: "instruments.id",
            value: this.form_data.instrument_id,
          },
        ],
        limit: "all",
        message: this.$bvToast,
      };

      // this.instrument_jig_collectiion = await getCollection({
      //   ...payload,
      //   collection_type: "instrument_jig",
      //   connections: ["jig"],
      // });
      this.task_collection = await getCollection({
        ...payload,
        collection_type: `${this.task_type}_task`,
        connections: [
          "instrument_instances",
          "analyses.units",
          "analyses.analyte",
        ],
      });
    },
    async init() {
      const { data } = await SampleRepository.getAnalytes(
        this.form_data.sample_id
      );
      this.instrument_collectiion = await getCollection({
        ...this.payload,
        collection_type: "instrument",
      });
      console.log('data', data)
      this.analyte_collection = data.data.length ? 
        [
          {id: null, name: 'Select an option...', disabled: true},  
          ...data.data
        ] 
        : this.default_option;
      this.loading = false;
    },
  },
  created() {
    this.loading = true;

    this.init();
  },
};
</script>
